/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../../widgets/livewidget/nls/StringResources',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/ChangeVisTypeActionAPI',
	'../../../widgets/livewidget/visSwitcher/VisChangerFlyoutView',
	'../../../lib/@waca/dashboard-common/dist/api/ContentActionsProviderAPI'
], function(_, stringResources, APIFactory, ChangeVisTypeActionAPI, VisChangerFlyoutView, ContentActionsProviderAPI) {

	return class ChangeVisTypeAction {

		constructor({ features }) {
			if(features) {
				this.dashboard = features.API;
				this.features = features;
				this.transaction = this.dashboard.getFeature('Transaction');
				this.visDefintions =  this.dashboard.getFeature('VisDefinitions');
				this._dashboardPreferences = features.DashboardPreferences;
				this._dashboardState = features.DashboardState;
				this.icons = features.Icons;
				features.ContentActions.registerProvider('changeVisTypeAction', this.getAPI());
			}
		}

		destroy() {
			this.dashboard = null;
			this.api = null;
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [ChangeVisTypeActionAPI, ContentActionsProviderAPI]);
			}
			return this.api;
		}

		_supportAction(idList, content) {
			const onlyOneElement = Boolean(idList.length === 1);

			const contentIsLiveWidget = Boolean(content && content.getType() === 'widget.live');

			const inSupportedMode = (this.dashboard.getMode() !== this.dashboard.MODES.EDIT_GROUP);

			const state = content && content.getFeature('state');
			const hasError = Boolean(state && !!state.getError());

			return onlyOneElement && contentIsLiveWidget && inSupportedMode && this._isInteractionEnabled('changeVis') && !hasError;
		}

		getContentActionList(idList) {
			const content = this.dashboard.getCanvas().getContent(idList[0]);
			if(this._supportAction(idList, content)) {
				// Vis definitions that do not expose a contextual change visualization toolbar in
				// consume mode. Reason as value in object.
				let visDefsNoChangeVisAction = {
					summary: 1, // Summary widget is intended to serve a single purpose of exposing a value, exposing the ability to change it in consume mode doesn't add value
					// Also its size and shape wouldn't be condusive to other widgets.
					dataPlayer: 1, // DataPlayer is a very specific size/shape and other widgets wouldn't render well in it's container
					list: 1, // Odd shape would cause most visualization changes to render oddly
					hierarchy2: 1, // Odd shape would cause most visualization changes to render oddly
					hierarchy3: 1 // Odd shape would cause most visualization changes to render oddly
				};

				let visualization = content.getFeature('Visualization');
				const changeVisIcon = this.icons.getIcon('visualizations-changeVisualization');

				let addChangeVisAction = true;

				if (this.dashboard.getMode() === this.dashboard.MODES.VIEW && visualization && visDefsNoChangeVisAction[visualization.getDefinition().getId()]) {
					addChangeVisAction = false;
				}

				const docked = this._dashboardPreferences.getPreference('isToolbarDocked') || this._dashboardState.getUiState().focus;

				let handler;
				if (addChangeVisAction) {
					let viewOptions = {
						height: 202,
						width: 402,
						content: content,
						dashboard: this.dashboard
					};
					let contribution = {
						name: 'changeVisTypeAction',
						label: stringResources.get('visChange'),
						icon: changeVisIcon.id,
						type: 'NextView',
						view: {
							module: 'dashboard-analytics/widgets/livewidget/visSwitcher/VisChangerFlyoutView',
							state: viewOptions
						},
						actions: {
							apply: this._changeVisType.bind(this, content)
						}
					};
					if (docked) {
						_.extend(contribution, {
							text: visualization.getDefinition().getLabel(),
							className: 'buttonWithText',
							callbacks: {
								registerCallback: (button) => {
									handler = ()=>{
										button.updateState({ text: visualization.getDefinition().getLabel() });
									};
									visualization.on('change:type', handler);
								},
								unregisterCallback: () => {
									visualization.off('change:type', handler);
								}
							}
						});
					}
					return [contribution];
				}
			}

			return [];
		}

		_isInteractionEnabled(name) {
			const config = this.dashboard.getAppConfig('interactions') || {};
			if (config.hasOwnProperty(name) && [false, 'false'].indexOf(config[name]) !== -1) {
				return false;
			}
			return true;
		}

		_trackSetType (content, type, options = {}) {
			if (options.recommendedCategory && this.features.LiveWidgetSegment) {
				const trackInfo = {
					category: options.recommendedCategory,
					visualization: content.getFeature('Visualization'),
					content: content,
					info: {
						visId: type
					}
				};
				this.features.LiveWidgetSegment.track(trackInfo);
			}
		}

		_changeVisType(content, type, options) {
			const recommender = content.getFeature('Visualization.SmartsRecommender');
			if (recommender){
				let promise;
				if (type === 'auto'){
					promise = recommender.recommendBestVisualization();
				} else {
					this._trackSetType(content, type, options);
					promise = recommender.setType(type);
				}

				return promise.then(() => {
					content.getFeature('Toolbar.internal').refresh();
				});
			}
		}

		openDialog(renderOptions, dialogOptions) {
			dialogOptions.actions = {
				apply: this._changeVisType.bind(this, dialogOptions.content)
			};
			if (!this.visChangeView) {
				this.visChangeView = new VisChangerFlyoutView(dialogOptions);
			}

			return this.visChangeView.render(renderOptions);
		}

		removeDialog () {
			if (this.visChangeView) {
				this.visChangeView.remove();
				this.visChangeView = null;
			}
		}

	};
});
